import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';

import AIManagersService from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import { ROUTES_URL } from 'src/constants/routes';
import { ReviewQueueList } from 'src/screens/SalesFunnel/SalesFunnelBoard';

import ConversationDetailsPanel from './ConversationDetailsPanel';
import useConversationDetailsRoute from './useConversationDetailsRoute';

import type { AIManagerConversation, AIManagerFunnelStageKey } from 'src/services/AIManagersService';

export default function ManagerWorkQueue({ managerId, active }: { managerId: string; active: boolean }) {
    const details = useConversationDetailsRoute();
    const client = useQueryClient();
    const queryKey = ['manager-work-review', managerId];
    const alive = useRef(true);
    const saving = useRef(false);
    const [movingId, setMovingId] = useState<number | null>(null);
    const [saveError, setSaveError] = useState('');
    const { data, isLoading, isError, isFetching, refetch } = useQuery(
        queryKey,
        () => AIManagersService.getReviewQueue(managerId),
        { enabled: active, staleTime: 30_000, retry: 1 }
    );

    useEffect(() => {
        alive.current = true;
        const subscription = EventService.subscribe('AI_MANAGERS_UPDATED', () => {
            void client.invalidateQueries(['manager-work-review', managerId]);
        });
        return () => {
            alive.current = false;
            subscription.unsubscribe();
        };
    }, [client, managerId]);

    const move = async (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => {
        if (saving.current || conversation.salesStage === stage) return;
        saving.current = true;
        setMovingId(conversation.id);
        setSaveError('');
        try {
            const updated = await AIManagersService.moveConversationStage(conversation.id, stage);
            client.setQueryData<AIManagerConversation[]>(queryKey, (items = []) =>
                items
                    .map((item) => (item.id === updated.id ? updated : item))
                    .filter((item) => item.status === 'human_review_required')
            );
            EventService.dispatch('AI_MANAGERS_UPDATED');
        } catch (_error) {
            if (alive.current) setSaveError('Не удалось изменить стадию. Попробуйте ещё раз.');
        } finally {
            saving.current = false;
            if (alive.current) setMovingId(null);
        }
    };

    return (
        <Stack spacing={2}>
            <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
            >
                <Typography
                    component="h2"
                    variant="h3"
                >
                    На проверку{data && !isError ? ` · ${data.length}` : ''}
                </Typography>
                <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                >
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<IconRefresh size={16} />}
                        disabled={isFetching || movingId !== null}
                        onClick={() => {
                            void refetch();
                        }}
                    >
                        Обновить
                    </Button>
                    <Button
                        component={Link}
                        to={`${ROUTES_URL.SALES_FUNNEL}?managerId=${encodeURIComponent(managerId)}`}
                        variant="outlined"
                        size="small"
                    >
                        Все диалоги
                    </Button>
                </Box>
            </Box>
            {saveError && (
                <Typography
                    role="alert"
                    color="var(--lm-danger-ink)"
                    variant="body2"
                >
                    {saveError}
                </Typography>
            )}
            {isError && (
                <Box role="alert">
                    <Typography variant="body2">Не удалось обновить очередь диалогов.</Typography>
                    {data && (
                        <Typography
                            variant="body2"
                            color="secondary.main"
                        >
                            Показаны ранее загруженные диалоги.
                        </Typography>
                    )}
                    <Button
                        disabled={isFetching}
                        onClick={() => {
                            void refetch();
                        }}
                    >
                        Повторить
                    </Button>
                </Box>
            )}
            {isLoading && !data && (
                <Box
                    role="status"
                    display="flex"
                    alignItems="center"
                    gap={1}
                >
                    <CircularProgress size={18} />
                    <Typography variant="body2">Загружаем диалоги…</Typography>
                </Box>
            )}
            {!isLoading && !isError && data?.length === 0 && (
                <Box sx={{ py: 4 }}>
                    <Typography
                        component="h3"
                        variant="h3"
                    >
                        Нет диалогов на проверку
                    </Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={1}
                    >
                        Остальные диалоги — в воронке менеджера.
                    </Typography>
                </Box>
            )}
            {!!data?.length && (
                <ReviewQueueList
                    conversations={data}
                    movingConversationId={movingId}
                    disabled={movingId !== null}
                    onMove={move}
                    onOpen={details.open}
                />
            )}
            {active && details.conversationId && (
                <ConversationDetailsPanel
                    key={`${managerId}:${details.conversationId}`}
                    managerId={managerId}
                    conversationId={details.conversationId}
                    onClose={details.close}
                />
            )}
        </Stack>
    );
}
